import { UserOutlined, MailOutlined, IdcardOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Form, Input, Card, Typography, Alert, Divider, Row, Col } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useAdministradoresProvider } from 'providers/Administradores/Administradores';
import React, { useState } from 'react';

const { Title, Text } = Typography;

const AdminUserForm = ({ initialValues, onSuccess, onCancel }) => {
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { update } = useAdministradoresProvider();

  const finishForm = async (values) => {
    setLoading(true);
    setError(null);

    try {
      const formValues = { ...values };

      // Migración rut→id: el backend identifica el usuario a actualizar por su id.
      formValues.id = initialValues.id;

      const response = await update(formValues);

      if (response && response.success !== false) {
        form.resetFields();
        if (onSuccess) onSuccess(response);
      } else {
        setError('Error al actualizar el administrador. Por favor, inténtalo de nuevo.');
      }
    } catch (err) {
      setError('Error al actualizar el administrador. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="max-w-2xl mx-auto">
      {/* Header del formulario */}
      <div className="text-center mb-6">
        <Title level={3} className="flex items-center justify-center mb-2">
          <UserOutlined className="mr-2 text-blue-500" />
          Editar Administrador
        </Title>
        <Text type="secondary">Actualiza la información del administrador de edificios</Text>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          className="mb-4"
          closable
          onClose={() => setError(null)}
        />
      )}

      <Card className="shadow-md">
        <Form
          form={form}
          onFinish={finishForm}
          layout="vertical"
          initialValues={initialValues}
          size="large"
        >
          {/* Información Personal */}
          <div className="mb-6">
            <Title level={5} className="flex items-center mb-4">
              <UserOutlined className="mr-2 text-blue-500" />
              Información Personal
            </Title>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={16}>
                <Form.Item
                  name="fullname"
                  label={
                    <span className="flex items-center">
                      <UserOutlined className="mr-1" />
                      Nombre Completo
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: 'El nombre completo es obligatorio',
                    },
                    {
                      min: 2,
                      message: 'El nombre debe tener al menos 2 caracteres',
                    },
                    {
                      max: 180,
                      message: 'El nombre no puede tener más de 180 caracteres',
                    },
                    {
                      pattern: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/,
                      message: 'El nombre solo puede contener letras y espacios',
                    },
                  ]}
                >
                  <Input
                    placeholder="Ingresa el nombre completo"
                    prefix={<UserOutlined className="text-gray-400" />}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="rut"
                  label={
                    <span className="flex items-center">
                      <IdcardOutlined className="mr-1" />
                      RUT
                    </span>
                  }
                >
                  <Input
                    disabled
                    placeholder="RUT"
                    prefix={<IdcardOutlined className="text-gray-400" />}
                    className="bg-gray-50"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="email"
              label={
                <span className="flex items-center">
                  <MailOutlined className="mr-1" />
                  Correo Electrónico
                </span>
              }
              rules={[
                {
                  required: true,
                  message: 'El correo electrónico es obligatorio',
                },
                {
                  type: 'email',
                  message: 'Debe ingresar un correo electrónico válido',
                },
                {
                  max: 80,
                  message: 'El correo no puede tener más de 80 caracteres',
                },
              ]}
            >
              <Input
                placeholder="correo@ejemplo.com"
                prefix={<MailOutlined className="text-gray-400" />}
              />
            </Form.Item>
          </div>

          <Divider />

          {/* La contraseña ya no se toca desde acá. Esta pantalla la reseteaba al RUT del
              usuario —un dato público— sin pedir nada a quien lo hacía. El cambio de
              contraseña vive en /admin/usuarios, con la acción "Cambiar contraseña", que
              exige la contraseña de quien lo realiza. */}
          <Alert
            message="¿Necesitas cambiarle la contraseña?"
            description="Se hace desde Usuarios, con la acción Cambiar contraseña."
            type="info"
            showIcon
            className="mb-6"
          />

          <Divider />

          {/* Botones de Acción */}
          <Form.Item className="mb-0">
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12}>
                <Button block size="large" onClick={onCancel} className="h-12">
                  Cancelar
                </Button>
              </Col>
              <Col xs={24} sm={12}>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  icon={<SaveOutlined />}
                  className="h-12"
                >
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AdminUserForm;
